import React, { useState, useEffect, useMemo, useRef } from "react";
import { loadCurrencySymbols } from "../../utils/currency";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useTheme } from "../../utils/theme";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  logoutUser,
  fetchCurrentUser,
  selectCurrentUser,
  selectPermissions,
} from "../../features/auth/authSlice";
import LayoutDashboard from "@mui/icons-material/Dashboard";
import Users from "@mui/icons-material/People";
import Building2 from "@mui/icons-material/Business";
import FileSearch from "@mui/icons-material/FindInPage";
import FileText from "@mui/icons-material/Description";
import Receipt from "@mui/icons-material/Receipt";
import FileOutput from "@mui/icons-material/UploadFile";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import CheckSquare from "@mui/icons-material/CheckBox";
import Settings from "@mui/icons-material/Settings";
import FileType from "@mui/icons-material/TextSnippet";
import UserCog from "@mui/icons-material/ManageAccounts";
import Shield from "@mui/icons-material/Shield";
import Sliders from "@mui/icons-material/Tune";
import Database from "@mui/icons-material/Storage";
import LogOut from "@mui/icons-material/Logout";
import VpnKey from "@mui/icons-material/VpnKey";
import ChangePasswordModal from "../../pages/auth/ChangePasswordModal";
import Bell from "@mui/icons-material/Notifications";
import Search from "@mui/icons-material/Search";
import Menu from "@mui/icons-material/Menu";
import X from "@mui/icons-material/Close";
import FileMinus from "@mui/icons-material/InsertDriveFile";
import FilePlus from "@mui/icons-material/NoteAdd";
import CreditCard from "@mui/icons-material/CreditCard";
import RotateCcw from "@mui/icons-material/Replay";
import DollarSign from "@mui/icons-material/AttachMoney";
import Percent from "@mui/icons-material/Percent";
import BarChart2 from "@mui/icons-material/BarChart";
import Inbox from "@mui/icons-material/Inbox";
import Truck from "@mui/icons-material/LocalShipping";
import Package from "@mui/icons-material/Inventory2";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Layers from "@mui/icons-material/Layers";
import Public from "@mui/icons-material/Public";
import api from "../../services/api";
import Sidebar from "./Sidebar";
import BottomNav from "./BottomNav";
import useSidebarState from "../../hooks/useSidebarState";

// Icon mapping for module display
const ICON_MAP = {
  LayoutDashboard: LayoutDashboard,
  Users: Users,
  Building2: Building2,
  FileSearch: FileSearch,
  FileText: FileText,
  Receipt: Receipt,
  FileOutput: FileOutput,
  ShoppingCart: ShoppingCart,
  CheckSquare: CheckSquare,
  Settings: Settings,
  FileType: FileType,
  UserCog: UserCog,
  Shield: Shield,
  Sliders: Sliders,
  Database: Database,
  FileMinus: FileMinus,
  FilePlus: FilePlus,
  CreditCard: CreditCard,
  RotateCcw: RotateCcw,
  DollarSign: DollarSign,
  Percent: Percent,
  BarChart2: BarChart2,
  Inbox: Inbox,
  Truck: Truck,
  Package: Package,
  TrendingUp: TrendingUp,
  Layers: Layers,
  Public: Public,
};

// Module slugs to hide from the sidebar nav (still reachable by direct URL)
const HIDDEN_NAV_MODULES = ["invoices"];

// Fallback hardcoded sidebar in case API fails
const FALLBACK_NAV_ITEMS = [
  {
    label: "Dashboard",
    path: "/dashboard",
    icon: LayoutDashboard,
    module: "dashboard",
  },
  { label: "Customers", path: "/customers", icon: Users, module: "customers" },
  { label: "Vendors", path: "/vendors", icon: Building2, module: "vendors" },
  { label: "Settings", path: "/company", icon: Settings, module: "company" },
];

export default function AppLayout({ children }) {
  useEffect(() => { loadCurrencySymbols(); }, []);       // $ and EUR signs next to amounts come from Currencies
  const [appTheme, toggleTheme] = useTheme();
  const { collapsed, toggle: toggleCollapsed } = useSidebarState();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("nav_open_groups") || "{}");
    } catch (e) {
      return {};
    }
  });
  const [loadingModules, setLoadingModules] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const permissions = useSelector(selectPermissions);

  useEffect(() => {
    const handleClickOutside = (ev) => {
      if (userMenuRef.current && !userMenuRef.current.contains(ev.target)) {
        setUserMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(ev.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Refresh permissions on app load — the login response is a snapshot taken
  // once; if an admin changes this user's role permissions afterward, this is
  // what picks the change up on the next page load/refresh.
  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);

  // Notifications: fetch unread count on mount + poll every 60s (no
  // websockets/channels in this stack, so polling is the pragmatic choice).
  const fetchUnreadCount = async () => {
    try {
      const res = await api.get("/notifications/unread_count/");
      setUnreadCount(res.data?.count || 0);
    } catch (err) {
      console.error("Failed to load unread notification count:", err);
    }
  };
  const fetchNotifications = async () => {
    try {
      const res = await api.get("/notifications/?page_size=20");
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setNotifications(data);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  };
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleBellClick = () => {
    setNotifOpen((v) => !v);
    setUserMenuOpen(false);
    if (!notifOpen) fetchNotifications();
  };

  const handleNotificationClick = async (n) => {
    setNotifOpen(false);
    if (!n.is_read) {
      try {
        await api.patch(`/notifications/${n.id}/`, { is_read: true });
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (err) {
        console.error("Failed to mark notification read:", err);
      }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async (ev) => {
    ev.stopPropagation();
    try {
      await api.post("/notifications/mark_all_read/");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications read:", err);
    }
  };

  // Fetch modules from API dynamically
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await api.get("/modules/?is_active=true&page_size=100");
        const data = Array.isArray(res.data)
          ? res.data
          : res.data.results || [];
        setModules(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
      } catch (err) {
        console.error("Failed to load modules:", err);
        setModules([]);
      } finally {
        setLoadingModules(false);
      }
    };
    fetchModules();
    api
      .get("/menu-groups/?is_active=true&page_size=100")
      .then((res) =>
        setGroups(Array.isArray(res.data) ? res.data : res.data.results || []),
      )
      .catch(() => setGroups([]));
  }, []);

  // Build navigation items from modules
  const navItems = useMemo(() => {
    if (modules.length === 0) return FALLBACK_NAV_ITEMS;

    return modules.map((module) => {
      const iconName = module.icon || "Package";
      const IconComponent = ICON_MAP[iconName] || Package;

      // Map slug to path
      const pathMap = {
        dashboard: "/dashboard",
        customers: "/customers",
        vendors: "/vendors",
        rfq: "/rfq",
        estimates: "/estimates",
        proforma_invoices: "/proforma-invoices",
        purchase_orders: "/purchase-orders",
        final_invoices: "/final-invoices",
        delivery_notes: "/delivery-notes",
        credit_notes: "/credit-notes",
        debit_notes: "/debit-notes",
        payments: "/payments",
        order_returns: "/order-returns",
        currencies: "/currencies",
        reports: "/reports",
        company: "/company",
        pdf_templates: "/pdf-templates",
        users: "/users",
        roles: "/roles",
        custom_fields: "/custom-fields",
        bulk_operations: "/bulk-operations",
        customer_pos: "/customer-pos",
        customer_rfqs: "/customer-rfqs",
        order_files: "/order-files",
        goods_receipts: "/goods-receipts",
        vessels: "/vessels",
        packing_lists: "/packing-lists",
        audit_log: "/audit-log",
        tax_rates: "/tax-rates",
        vendor_estimates: "/vendor-estimates",
        vendor_proformas: "/vendor-proformas",
        vendor_final_invoices: "/vendor-final-invoices",
        vendor_commercial_invoices: "/vendor-commercial-invoices",
        modules: "/modules",
        inventory: "/inventory/items",
        commercial_invoices: "/commercial-invoices",
      };

      return {
        label: module.name,
        path: pathMap[module.slug] || `/${module.slug}`,
        icon: IconComponent,
        module: module.slug,
        slug: module.slug,
        group: module.group || null,
        order: module.order || 0,
      };
    });
  }, [modules]);

  // Sidebar tree: top-level modules and group dropdowns, interleaved by their `order`
  const navTree = useMemo(() => {
    const known = new Set(groups.map((g) => g.id));
    const tree = navItems
      .filter((it) => !it.group || !known.has(it.group))
      .map((it) => ({ type: "item", order: it.order ?? 0, item: it }));
    groups.forEach((g) => {
      tree.push({
        type: "group",
        order: g.order || 0,
        id: g.id,
        label: g.name,
        icon: ICON_MAP[g.icon] || Layers,
        items: navItems.filter((it) => it.group === g.id),
      });
    });
    return tree.sort((a, b) => a.order - b.order);
  }, [navItems, groups]);

  const toggleGroup = (id, forceOpen) => {
    setOpenGroups((prev) => {
      const next = {
        ...prev,
        [id]: forceOpen === undefined ? !prev[id] : forceOpen,
      };
      try {
        localStorage.setItem("nav_open_groups", JSON.stringify(next));
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  };

  // open the dropdown that contains the page being viewed
  useEffect(() => {
    const g = navTree.find(
      (n) =>
        n.type === "group" &&
        n.items.some((it) => location.pathname.startsWith(it.path)),
    );
    if (g && !openGroups[g.id]) toggleGroup(g.id, true);
    // eslint-disable-next-line
  }, [location.pathname, navTree]);

  const hasAccess = (moduleSlug) => {
    if (!moduleSlug) return true;
    if (user?.is_superuser) return true;
    return permissions?.[moduleSlug]?.includes("can_view");
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  // sidebar badges: what needs attention (refreshed every 3 minutes and when the tab is shown again)
  const [badges, setBadges] = useState({});
  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get("/reports/nav-badges/").then((r) => alive && setBadges(r.data || {})).catch(() => {});
    load();
    const timer = setInterval(load, 180000);
    const onVisible = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  // phone drawer: the page behind does not scroll, Esc closes it, a swipe from the left edge opens it
  const swipe = useRef(null);
  useEffect(() => {
    document.documentElement.classList.toggle("nav-open", mobileOpen);
    return () => document.documentElement.classList.remove("nav-open");
  }, [mobileOpen]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    let edge = null;
    const onStart = (e) => {
      const t = e.touches[0];
      edge = window.innerWidth <= 768 && t.clientX < 18 ? { x: t.clientX, y: t.clientY } : null;
    };
    const onEnd = (e) => {
      if (!edge) return;
      const t = e.changedTouches[0];
      if (t.clientX - edge.x > 70 && Math.abs(t.clientY - edge.y) < 60) setMobileOpen(true);
      edge = null;
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, []);
  // the bottom bar steps aside while a form field is being typed in (it would sit on top of the keyboard)
  useEffect(() => {
    const field = (el) => el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
    const on = (e) => field(e.target) && document.documentElement.classList.add("typing");
    const off = () => document.documentElement.classList.remove("typing");
    document.addEventListener("focusin", on);
    document.addEventListener("focusout", off);
    return () => { document.removeEventListener("focusin", on); document.removeEventListener("focusout", off); off(); };
  }, []);

  // Safety net for phone browsers that draw the sticky top bar over the start of the page (the first lines then hide
  // under it even at the very top): if the page content starts above the bottom of the bar, push it down by the difference.
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  useEffect(() => {
    const fix = () => {
      const header = headerRef.current;
      const content = contentRef.current;
      if (!header || !content) return;
      content.style.marginTop = "0px";
      const overlap = header.offsetHeight - (content.getBoundingClientRect().top + window.scrollY);
      content.style.marginTop = overlap > 1 ? `${Math.ceil(overlap)}px` : "0px";
    };
    fix();
    const timers = [setTimeout(fix, 300), setTimeout(fix, 1200)];
    window.addEventListener("resize", fix);
    window.addEventListener("orientationchange", fix);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", fix);
      window.removeEventListener("orientationchange", fix);
    };
  }, [location.pathname]);

  const sidebarProps = {
    navTree, hasAccess, hiddenModules: HIDDEN_NAV_MODULES, collapsed, pathname: location.pathname, openGroups, toggleGroup,
    onToggleCollapse: toggleCollapsed, onNavigate: () => setMobileOpen(false), user, onLogout: handleLogout, badges,
    logoSrc: `${process.env.PUBLIC_URL}/crmlogo-icon.png`,
  };

  return (
    <div className={`app-layout ${collapsed ? "sidebar-collapsed" : ""}`}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Desktop Sidebar */}
      <aside className="sidebar desktop-sidebar">
        <Sidebar {...sidebarProps} mobile={false} />
      </aside>

      {/* Mobile drawer */}
      <aside
        className={`sidebar mobile-sidebar ${mobileOpen ? "open" : ""}`}
        aria-hidden={!mobileOpen}
        onTouchStart={(e) => { swipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
        onTouchEnd={(e) => {
          const t = e.changedTouches[0];
          if (swipe.current && swipe.current.x - t.clientX > 70 && Math.abs(t.clientY - swipe.current.y) < 60) setMobileOpen(false);
          swipe.current = null;
        }}
      >
        <Sidebar {...sidebarProps} mobile />
      </aside>

      {/* Main content */}
      <div className="main-content">
        {/* Top Header */}
        <header className="top-header" ref={headerRef}>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <div className="header-search">
            <Search size={16} />
            <input type="text" placeholder="Search..." />
          </div>

          <div className="header-actions">
            <button
              className="icon-btn"
              onClick={() => toggleTheme()}
              title={appTheme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {appTheme === "dark" ? <LightModeIcon style={{ fontSize: 18 }} /> : <DarkModeIcon style={{ fontSize: 18 }} />}
            </button>
            <div className="dropdown-wrap" ref={notifRef}>
              <button
                className="icon-btn notif-bell-btn"
                onClick={handleBellClick}
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="notif-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="dropdown-panel notif-panel">
                  <div className="dropdown-header notif-header">
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        className="notif-mark-all"
                        onClick={handleMarkAllRead}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="dropdown-empty">No new notifications</div>
                  ) : (
                    <div className="notif-list">
                      {notifications.map((n) => (
                        <button
                          key={n.id}
                          className={`notif-row ${n.is_read ? "" : "notif-row-unread"}`}
                          onClick={() => handleNotificationClick(n)}
                        >
                          {!n.is_read && <span className="notif-dot" />}
                          <div className="notif-row-body">
                            <div className="notif-row-title">{n.title}</div>
                            {n.message && (
                              <div className="notif-row-message">
                                {n.message}
                              </div>
                            )}
                            <div className="notif-row-time">
                              {new Date(n.created_at).toLocaleString()}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="dropdown-wrap" ref={userMenuRef}>
              <div
                className="header-user"
                onClick={() => {
                  setUserMenuOpen((v) => !v);
                  setNotifOpen(false);
                }}
              >
                <div className="user-avatar small">
                  {(
                    user?.first_name?.[0] ||
                    user?.email?.[0] ||
                    "U"
                  ).toUpperCase()}
                </div>
              </div>
              {userMenuOpen && (
                <div className="dropdown-panel user-panel">
                  <div className="dropdown-header">
                    <span className="user-panel-name">
                      {user?.first_name || user?.username || "User"}
                    </span>
                    <span className="user-panel-email">{user?.email}</span>
                  </div>
                  <div className="user-panel-info">
                    <div className="user-panel-row">
                      <span>Role</span>
                      <span>
                        {user?.is_superuser ? "Super Administrator" : "User"}
                      </span>
                    </div>
                    <div className="user-panel-row">
                      <span>Joined</span>
                      <span>
                        {user?.date_joined
                          ? new Date(user.date_joined).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="user-panel-row">
                      <span>Phone</span>
                      <span>{user?.phone || "Not provided"}</span>
                    </div>
                  </div>
                  <button
                    className="dropdown-item"
                    style={{ color: "#2D3748" }}
                    onClick={() => {
                      setUserMenuOpen(false);
                      setShowPasswordModal(true);
                    }}
                  >
                    <VpnKey size={15} />
                    Change Password
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <LogOut size={15} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <ChangePasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
        />

        {/* Page content */}
        <main className="page-content" ref={contentRef}>{children}</main>
      </div>

      <BottomNav
        navTree={navTree} hasAccess={hasAccess} hiddenModules={HIDDEN_NAV_MODULES} pathname={location.pathname} badges={badges}
        onGroup={(id) => { toggleGroup(id, true); setMobileOpen(true); }} onMore={() => setMobileOpen(true)}
      />

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --sidebar-w: 240px;
          --sidebar-collapsed: 64px;
          --header-h: 60px;
          --blue-dark: #1E3A5F;
          --blue: #2E86AB;
          --blue-light: #D6E4F0;
          --accent: #F18F01;
          --bg: #F5F7FA;
          --white: #ffffff;
          --border: #E2E8F0;
          --text: #2C3E50;
          --text-muted: #718096;
          --sidebar-bg: #1A2E4A;
          --sidebar-text: #CBD5E0;
          --sidebar-active: #2E86AB;
          --sidebar-hover: rgba(255,255,255,0.06);
          --radius: 8px;
          --shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08);
          --shadow-lg: 0 4px 16px rgba(0,0,0,0.12);
        }
        body { font-family: 'Inter', -apple-system, sans-serif; background: var(--bg); color: var(--text); }

        .app-layout { display: flex; min-height: 100vh; }

        /* SIDEBAR */
        .sidebar {
          width: var(--sidebar-w);
          min-height: 100vh;
          background: var(--sidebar-bg);
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          transition: width 0.25s ease;
          position: fixed;
          top: 0; left: 0; bottom: 0;
          z-index: 100;
          overflow: hidden;
        }
        .app-layout.sidebar-collapsed .sidebar { width: var(--sidebar-collapsed); }
        .sidebar-inner { display: flex; flex-direction: column; height: 100%; }

        /* Logo */
        .sidebar-logo {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 16px; border-bottom: 1px solid rgba(255,255,255,0.08);
          min-height: 64px;
        }
        .logo-brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .logo-img { width: 68px; height: auto; flex-shrink: 0; display: block; }
        .logo-text { display: flex; flex-direction: column; }
        .logo-main { font-size: 18px; font-weight: 800; color: white; letter-spacing: -0.5px; }
        .logo-sub { font-size: 10px; color: #64a0c8; text-transform: uppercase; letter-spacing: 0.5px; }
        .collapse-btn {
          background: rgba(255,255,255,0.08); border: none; color: var(--sidebar-text);
          width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .collapse-btn:hover { background: rgba(255,255,255,0.15); color: white; }

        /* Nav */
        .sidebar-nav { flex: 1; min-height: 0; padding: 12px 8px; overflow-y: auto; }
        .nav-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 8px 8px; }
        .nav-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 10px; border-radius: 6px; margin-bottom: 2px;
          color: var(--sidebar-text); text-decoration: none;
          transition: all 0.15s; white-space: nowrap; overflow: hidden;
          font-size: 13.5px; font-weight: 500;
        }
        .nav-item:hover { background: var(--sidebar-hover); color: white; }
        .nav-item.active { background: var(--sidebar-active); color: white; }
        .nav-icon { flex-shrink: 0; }
        .nav-group-btn { width: 100%; background: none; border: none; cursor: pointer; font-family: inherit; }
        .nav-group-btn.has-active { color: white; }
        .nav-sub { margin: 0 0 6px 14px; padding-left: 8px; border-left: 1px solid rgba(255,255,255,0.12); }
        .nav-sub-item { padding: 7px 10px; font-size: 13px; }
        .nav-label { overflow: hidden; }

        /* Footer */
        .sidebar-footer {
          padding: 12px 8px; border-top: 1px solid rgba(255,255,255,0.08);
          display: flex; align-items: center; gap: 8px;
        }
        .user-info { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
        .user-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: var(--sidebar-active); color: white;
          font-size: 13px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .user-avatar.small { width: 28px; height: 28px; background: var(--blue); font-size: 12px; }
        .user-details { display: flex; flex-direction: column; min-width: 0; }
        .user-name { font-size: 13px; font-weight: 600; color: white; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .user-role { font-size: 11px; color: var(--sidebar-text); }
        .logout-btn {
          background: none; border: none; color: var(--sidebar-text); cursor: pointer;
          padding: 6px; border-radius: 6px; display: flex; align-items: center;
          flex-shrink: 0;
        }
        .logout-btn:hover { background: rgba(255,255,255,0.08); color: #fc8181; }

        /* MAIN CONTENT */
        .main-content {
          flex: 1;
          min-width: 0; /* let wide tables scroll inside their card instead of stretching the page */
          margin-left: var(--sidebar-w);
          display: flex; flex-direction: column;
          min-height: 100vh;
          transition: margin-left 0.25s ease;
        }
        .app-layout.sidebar-collapsed .main-content { margin-left: var(--sidebar-collapsed); }

        /* HEADER */
        .top-header {
          position: sticky; top: 0; z-index: 50;
          height: var(--header-h);
          background: var(--white);
          border-bottom: 1px solid var(--border);
          display: flex; align-items: center; gap: 16px;
          padding: 0 24px;
          box-shadow: var(--shadow);
        }
        .mobile-menu-btn {
          display: none; background: none; border: none; cursor: pointer;
          color: var(--text); padding: 6px; border-radius: 6px;
        }
        .header-search {
          flex: 1; max-width: 400px;
          display: flex; align-items: center; gap: 8px;
          background: var(--bg); border: 1px solid var(--border);
          border-radius: 8px; padding: 7px 12px;
          color: var(--text-muted);
        }
        .header-search input {
          border: none; background: none; outline: none; font-size: 13.5px;
          color: var(--text); width: 100%;
        }
        .header-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }
        .icon-btn {
          background: none; border: none; cursor: pointer;
          color: var(--text-muted); padding: 8px; border-radius: 8px;
          display: flex; align-items: center;
        }
        .icon-btn:hover { background: var(--bg); color: var(--text); }
        .notif-bell-btn { position: relative; }
        .notif-badge {
          position: absolute; top: 2px; right: 2px; min-width: 16px; height: 16px;
          padding: 0 3px; border-radius: 8px; background: #DC2626; color: #fff;
          font-size: 10px; font-weight: 700; line-height: 16px; text-align: center;
        }

        .dropdown-wrap { position: relative; }
        .header-user { cursor: pointer; display: flex; align-items: center; border-radius: 8px; padding: 2px; }
        .header-user:hover { background: var(--bg); }
        .dropdown-panel {
          position: absolute; top: calc(100% + 8px); right: 0; z-index: 50;
          background: #fff; border: 1px solid #E2E8F0; border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12); min-width: 220px; overflow: hidden;
        }
        .dropdown-header {
          padding: 12px 14px; border-bottom: 1px solid #E2E8F0;
          display: flex; flex-direction: column; gap: 2px;
        }
        .user-panel-name { font-size: 13.5px; font-weight: 700; color: var(--text); }
        .user-panel-email { font-size: 12px; color: var(--text-muted); word-break: break-all; }
        .user-panel { min-width: 250px; }
        .user-panel-info { padding: 4px 14px 8px; border-bottom: 1px solid #E2E8F0; }
        .user-panel-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 5px 0; font-size: 12.5px; gap: 12px;
        }
        .user-panel-row span:first-child { color: var(--text-muted); }
        .user-panel-row span:last-child { color: var(--text); font-weight: 600; text-align: right; }
        .dropdown-empty { padding: 16px 14px; font-size: 13px; color: var(--text-muted); text-align: center; }
        .notif-panel { min-width: 320px; max-width: 360px; }
        .notif-header {
          flex-direction: row !important; align-items: center; justify-content: space-between;
          font-size: 13.5px; font-weight: 700; color: var(--text);
        }
        .notif-mark-all {
          background: none; border: none; cursor: pointer; color: var(--blue);
          font-size: 12px; font-weight: 600; padding: 0;
        }
        .notif-mark-all:hover { text-decoration: underline; }
        .notif-list { max-height: 340px; overflow-y: auto; }
        .notif-row {
          width: 100%; display: flex; align-items: flex-start; gap: 8px;
          padding: 10px 14px; background: none; border: none; border-bottom: 1px solid #F1F5F9;
          cursor: pointer; text-align: left;
        }
        .notif-row:hover { background: var(--bg); }
        .notif-row-unread { background: #F0F9FF; }
        .notif-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--blue); margin-top: 5px; flex-shrink: 0; }
        .notif-row-body { min-width: 0; flex: 1; }
        .notif-row-title { font-size: 13px; font-weight: 600; color: var(--text); }
        .notif-row-message { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
        .notif-row-time { font-size: 11px; color: #A0AEC0; margin-top: 4px; }
        .dropdown-item {
          width: 100%; display: flex; align-items: center; gap: 8px;
          padding: 10px 14px; background: none; border: none; cursor: pointer;
          font-size: 13.5px; font-weight: 600; color: #DC2626; text-align: left;
        }
        .dropdown-item:hover { background: #FEF2F2; }

        /* PAGE CONTENT */
        .page-content { flex: 1; min-width: 0; max-width: 100%; padding: 24px; overflow-x: hidden; }

        /* Sidebar polish */
        .sidebar { height: 100vh; height: 100dvh; min-height: 0; }
        .nav-item { position: relative; }
        .nav-item.active::before {
          content: ""; position: absolute; left: 0; top: 7px; bottom: 7px; width: 3px; border-radius: 0 3px 3px 0; background: #fff; opacity: .9;
        }
        .nav-item:focus-visible, .collapse-btn:focus-visible, .logout-btn:focus-visible, .bn-tab:focus-visible {
          outline: 2px solid #7DD3FC; outline-offset: -2px;
        }
        .nav-sub-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .2s ease; }
        .nav-sub-wrap.open { grid-template-rows: 1fr; }
        .nav-sub-inner { overflow: hidden; min-height: 0; }
        .nav-badge {
          margin-left: auto; min-width: 20px; height: 18px; padding: 0 6px; border-radius: 9px; background: #F59E0B; color: #1A2E4A;
          font-size: 10.5px; font-weight: 800; line-height: 18px; text-align: center; flex-shrink: 0;
        }
        .nav-group-btn .nav-chevron { margin-left: 4px; }
        .nav-badge-dot { position: absolute; top: 4px; right: 6px; min-width: 8px; width: 8px; height: 8px; padding: 0; font-size: 0; margin: 0; background: #F87171; }
        .nav-flyout {
          position: fixed; left: calc(var(--sidebar-collapsed) - 2px); z-index: 120; min-width: 210px; padding: 8px;
          overflow-y: auto; overscroll-behavior: contain; max-height: calc(100vh - 16px);
          background: var(--sidebar-bg); border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; box-shadow: 0 10px 28px rgba(0,0,0,0.35);
        }
        .nav-flyout-title { padding: 6px 10px 8px; font-size: 11px; letter-spacing: .6px; text-transform: uppercase; color: #64a0c8; font-weight: 700; }
        .nav-flyout .nav-sub-item { padding: 8px 10px; }
        .app-layout.sidebar-collapsed .sidebar { overflow: visible; }
        .app-layout.sidebar-collapsed .sidebar-footer { flex-direction: column; gap: 6px; padding: 10px 4px; }
        .app-layout.sidebar-collapsed .user-info { flex: none; }
        .app-layout.sidebar-collapsed .sidebar-nav { overflow: visible; }
        html.nav-open, html.nav-open body { overflow: hidden; }

        /* Bottom bar (phones) */
        .bottom-nav { display: none; }
        .bn-tab {
          position: relative; flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
          padding: 6px 2px; border: none; background: none; color: var(--text-muted); font: inherit; font-size: 10.5px; font-weight: 600;
          text-decoration: none; cursor: pointer;
        }
        .bn-tab span:not(.bn-badge) { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .bn-tab.active { color: var(--blue); }
        .bn-badge {
          position: absolute; top: 3px; left: calc(50% + 6px); min-width: 16px; height: 16px; padding: 0 4px; border-radius: 8px;
          background: #DC2626; color: #fff; font-size: 10px; font-weight: 700; line-height: 16px; text-align: center;
        }

        @media (pointer: coarse) {
          .nav-item { min-height: 44px; }
          .nav-sub-item { min-height: 40px; }
          .collapse-btn, .logout-btn { min-width: 40px; min-height: 40px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sidebar, .main-content, .nav-sub-wrap, .mobile-sidebar { transition: none !important; }
        }

        /* Mobile sidebar */
        .mobile-sidebar { display: none; }
        .mobile-overlay { display: none; }

        @media (max-width: 768px) {
          .desktop-sidebar { display: none; }
          .mobile-sidebar {
            display: flex; position: fixed;
            transform: translateX(-100%); transition: transform 0.25s ease;
          }
          .mobile-sidebar.open { transform: translateX(0); }
          .mobile-overlay {
            display: block; position: fixed; inset: 0;
            background: rgba(0,0,0,0.5); z-index: 90;
          }
          .main-content { margin-left: 0 !important; }
          .mobile-menu-btn { display: flex; }
          .page-content { padding: 16px; }
          .mobile-sidebar { width: var(--sidebar-w) !important; max-width: 85vw; padding-bottom: env(safe-area-inset-bottom); }
          .mobile-sidebar .sidebar-nav { overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
          .bottom-nav {
            display: flex; position: fixed; left: 0; right: 0; bottom: 0; z-index: 80; height: calc(58px + env(safe-area-inset-bottom));
            padding-bottom: env(safe-area-inset-bottom); background: var(--white); border-top: 1px solid var(--border);
            box-shadow: 0 -2px 10px rgba(0,0,0,0.06);
          }
          html.typing .bottom-nav, html.nav-open .bottom-nav { display: none; }
          .page-content { padding-bottom: calc(76px + env(safe-area-inset-bottom)); }
          .mobile-sidebar .collapse-btn { display: none; }
          .top-header { padding: 0 12px; gap: 8px; }
          .header-search { min-width: 0; max-width: none; }
        }
        @media (max-width: 600px) {
          .dropdown-wrap { position: static; }
          .dropdown-panel {
            position: fixed; top: calc(var(--header-h) + 6px); left: 8px; right: 8px;
            min-width: 0 !important; max-width: none !important;
          }
          .notif-list { max-height: 60vh; }
        }
      `}</style>
    </div>
  );
}
